<template>
  <SideBarLayoutContent>
    <template #top-bar>
      <h1 class="text-display-small grow">{{ $t('mainNavigation.coach-overview') }}</h1>
      <div class="flex flex-row gap-1">
        <BaseTextButton size="M" color="tonal" icon="person_add" @click="openAddCoacheesDialog">{{
          $t('coachees.addAction')
        }}</BaseTextButton>
        <BaseMenu v-if="isDialogModuleActive" :items="menuItems" v-slot="{ open }">
          <BaseIconButton size="M" width="narrow" icon="more_vert" :force-pressed="open" />
        </BaseMenu>
      </div>
    </template>
    <LoadingWrapper :state="status">
      <div v-if="rejectedCoachings.length" class="mb-10 flex flex-col gap-0.5">
        <RejectedCoachingItem
          v-for="rejectedCoaching in rejectedCoachings"
          :key="rejectedCoaching.coachee.id"
          :coachee="rejectedCoaching.coachee"
          @delete-coaching="deleteCoaching(rejectedCoaching.coachee.id)"
          @resend-coaching-invitation="resendCoachingInvitation(rejectedCoaching.coachee.id)"
        />
      </div>
      <template v-if="activeCoachees.length">
        <ul class="flex flex-col gap-0.5">
          <li
            v-for="coachee in activeCoachees"
            :key="coachee.coachee.id"
            class="bg-surface-container rounded-small first:rounded-t-large last:rounded-b-large group relative cursor-pointer overflow-hidden px-4 py-3"
            @click="navigateToCoachee(coachee.coachee.id)"
          >
            <div class="flex flex-row items-center gap-4">
              <div class="flex grow flex-row items-center">
                <BaseMemberAvatar :member="coachee.coachee" class="mr-4" />
                <div class="flex grow flex-col">
                  <span class="text-title-medium">{{ memberFullName(coachee.coachee) }}</span>
                  <span
                    v-if="coachee.currentDialog"
                    class="text-body-medium text-on-surface-variant mb-1"
                    >Aktueller Dialog<span v-if="coachee.currentDialog.plannedAt"
                      >・{{
                        useDisplayDate(coachee.currentDialog.plannedAt, {
                          dayName: 'short',
                          monthName: 'short',
                        }).value
                      }}</span
                    ></span
                  >
                </div>
              </div>
              <CoacheeDialogTimelineCompact
                v-if="
                  coachee.currentDialog &&
                  currentOrganization.modules.includes(ApplicationModule.DIALOG)
                "
                :dialog="coachee.currentDialog"
              />
              <BaseIcon size="24" class="text-outline -mr-2 shrink-0"
                >keyboard_arrow_right</BaseIcon
              >
            </div>
            <BaseStateLayer />
          </li>
        </ul>
      </template>
      <template v-if="pendingCoachees.length">
        <h3 class="text-title-medium text-on-surface-variant mt-6 mb-1">Ausstehende Einladungen</h3>
        <ul class="flex flex-col gap-0.5">
          <li
            v-for="coachee in pendingCoachees"
            :key="coachee.coachee.id"
            class="bg-surface-container rounded-small first:rounded-t-large last:rounded-b-large group relative cursor-pointer overflow-hidden px-4 py-3"
            @click="navigateToCoachee(coachee.coachee.id)"
          >
            <div class="flex flex-row items-center">
              <BaseMemberAvatar :member="coachee.coachee" class="mr-4" />
              <div class="flex grow flex-col">
                <span class="text-title-medium">{{ memberFullName(coachee.coachee) }}</span>
              </div>
              <BaseIcon size="24" class="text-outline -mr-2 ml-6 shrink-0"
                >keyboard_arrow_right</BaseIcon
              >
            </div>
            <BaseStateLayer />
          </li>
        </ul>
      </template>
      <template v-if="allCoachees.length === 0">
        <EmptyPage
          :title="$t('coachees.empty.title')"
          :description="$t('coachees.empty.description')"
          icon="people"
        >
          <BaseTextButton size="M" color="tonal" icon="person_add" @click="openAddCoacheesDialog">{{
            $t('coachees.addAction')
          }}</BaseTextButton>
        </EmptyPage>
      </template>
      <AddCoacheesDialog
        :is-open="isOpenAddCoacheesDialog"
        @close="closeAddCoacheesDialog"
        @add-coachees="addCoachees"
      />
      <EditDialogSettingDialog
        :is-open="isOpenEditDialogSettingDialog"
        @close="closeEditDialogSettingDialog"
      />
    </LoadingWrapper>
  </SideBarLayoutContent>
</template>
<script lang="ts" setup>
  import SideBarLayoutContent from '../../../core/components/layouts/SideBarLayoutContent.vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import RejectedCoachingItem from '../components/RejectedCoachingItem.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import { useAllCoachees } from '../composables/useAllCoachees'
  import { useMutateCoachee } from '../composables/useMutateCoachee'
  import EmptyPage from '../../../core/components/empty-states/EmptyPage.vue'
  import { useDialog } from '../../../core/components/base/dialog/use-dialog'
  import BaseMemberAvatar from '@/core/components/base/avatar/BaseMemberAvatar.vue'
  import CoacheeDialogTimelineCompact from '../components/CoacheeDialogTimelineCompact.vue'
  import { useDisplayDate } from '@/core/composables/use-display-date'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import { useRouter } from 'vue-router'
  import BaseStateLayer from '@/core/components/base/state-layer/BaseStateLayer.vue'
  import { computed, watch } from 'vue'
  import { useUserStore } from '@/stores/user.store'
  import { usePermission } from '@/core/composables/use-permission'
  import { Permission } from '@/core/models/user'
  import { ApiRequestStatus } from '@/core/models/api/api-request-status'
  import BaseMenu from '@/core/components/base/menu/BaseMenu.vue'
  import BaseIconButton from '@/core/components/base/button/icon-button/BaseIconButton.vue'
  import EditDialogSettingDialog from '@/modules/coachees/components/EditDialogSettingDialog.vue'
  import { useApplicationModule } from '@/core/composables/use-application-module'
  import { ApplicationModule } from '@/core/models/organization'
  import { useOrganizationStore } from '@/stores/organization.store'
  import AddCoacheesDialog from '../components/AddCoacheesDialog.vue'
  import { useMutateCoachees } from '../composables/useMutateCoachees'
  import { memberFullName } from '@/core/models/member/member-name'

  const router = useRouter()
  const { user } = useUserStore()
  const { can } = usePermission()
  const { hasModule } = useApplicationModule()
  const { currentOrganization } = useOrganizationStore()
  const { activeCoachees, rejectedCoachings, pendingCoachees, allCoachees, status } =
    useAllCoachees()
  const { deleteCoaching, resendCoachingInvitation } = useMutateCoachee()

  const { addCoachees } = useMutateCoachees()

  const isDialogModuleActive = computed(() => hasModule(ApplicationModule.DIALOG))

  const shouldRedirectToCoachOnboarding = computed(
    () =>
      status.value === ApiRequestStatus.Success &&
      !user.settings.coachOnboardingCompleted &&
      can(Permission.COACHING_ASSIGN),
  )

  const {
    isOpen: isOpenAddCoacheesDialog,
    openDialog: openAddCoacheesDialog,
    closeDialog: closeAddCoacheesDialog,
  } = useDialog()

  const {
    isOpen: isOpenEditDialogSettingDialog,
    openDialog: openEditDialogSettingDialog,
    closeDialog: closeEditDialogSettingDialog,
  } = useDialog()

  const menuItems = computed(() => [
    {
      id: 'edit-dialog-setting',
      title: 'Dialog-Themen bearbeiten',
      leadingIcon: 'edit',
      callback: () => openEditDialogSettingDialog(),
    },
  ])

  const navigateToCoachee = (coacheeId: string) => {
    router.push({
      name: 'coachee',
      params: { id: coacheeId },
    })
  }

  watch(shouldRedirectToCoachOnboarding, (shouldRedirect) => {
    if (shouldRedirect) {
      router.replace({
        name: 'coach-onboarding',
      })
    }
  })
</script>
